/**
 * Real, linkable routes (`createBrowserRouter`).
 *
 * The guard is the interesting part. It reads the session through
 * `session.useSession()` — `useSyncExternalStore` underneath — so **clearing
 * the session actually re-renders**. A guard that reads `localStorage` during
 * render subscribes to nothing: the app would go on rendering while every
 * request 401s (SSO_INTEGRATION §3.4, the second of the three mistakes).
 */

import { SsoRedirect } from '@fiveox/shell'
import { useEffect, type ReactNode } from 'react'
import { createBrowserRouter, Outlet } from 'react-router'

import { App } from './App'
import { Callback } from './routes/Callback'
import { Clients } from './routes/Clients'
import { Overview } from './routes/Overview'
import { PeriodPicker } from './routes/workspace/PeriodPicker'
import { AliasPage } from './routes/workspace/AliasPage'
import { PartyDetailPage } from './routes/workspace/PartyDetailPage'
import { RawDataPage } from './routes/workspace/RawDataPage'
import { ReportsPage } from './routes/workspace/ReportsPage'
import { ReviewQueuePage } from './routes/workspace/ReviewQueuePage'
import { AuditPage } from './routes/workspace/AuditPage'
import { SettingsPage } from './routes/workspace/SettingsPage'
import { ReconciliationPage } from './routes/workspace/ReconciliationPage'
import { UploadPage } from './routes/workspace/UploadPage'
import { Workspace } from './workspace/WorkspaceFrame'
import { RETURN_KEY, ssoConfig } from './lib/sso-config'
import { session } from './lib/session'
import { armSessionEnd } from './lib/session-end'

function RequireSession({ children }: { children: ReactNode }) {
  const current = session.useSession()

  // **The one place a session ends** (ADR-0012). Here because this component
  // already owns the *no session → SSO* decision and is mounted exactly once;
  // `armSessionEnd` returns its own unsubscribe, so the effect's cleanup is
  // already correct. It must sit ABOVE the early return — an effect below one
  // never runs on the render that matters.
  useEffect(armSessionEnd, [])

  // No session — hand the browser to accounts. **This app has no login screen**
  // (ADR-0048); `SsoRedirect` stashes the deep link and starts the PKCE flow.
  if (!current) {
    return <SsoRedirect config={ssoConfig()} returnKey={RETURN_KEY} />
  }
  return <>{children}</>
}

function Shell() {
  return (
    <RequireSession>
      <App>
        <Outlet />
      </App>
    </RequireSession>
  )
}

export const router = createBrowserRouter([
  // `/callback` sits **outside** the guard: it is the route that creates the
  // session, so guarding it would redirect the sign-in away from its own
  // completion.
  { path: '/callback', element: <Callback /> },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Overview /> },
      { path: 'clients', element: <Clients /> },
      // The workspace (P7): everything about ONE client's ONE FY lives under
      // `/w/:clientId/:periodId/*` behind the in-page frame — the
      // predecessor's information architecture, kept (ADR-0010 restyles, it
      // does not rearrange). `/w/:clientId` first asks WHICH year.
      { path: 'w/:clientId', element: <PeriodPicker /> },
      {
        path: 'w/:clientId/:periodId',
        element: <Workspace />,
        children: [
          // the workspace opens on Upload — the screen a fresh session needs
          // first, and an honest one when everything is already loaded
          { index: true, element: <UploadPage /> },
          { path: 'upload', element: <UploadPage /> },
          { path: 'raw-data', element: <RawDataPage /> },
          { path: 'aliases', element: <AliasPage /> },
          { path: 'reconciliation', element: <ReconciliationPage /> },
          // the party drill-through keeps the reconciliation prefix so the
          // tab bar stays on Reconciliation while it is open
          { path: 'reconciliation/parties/:partyId', element: <PartyDetailPage /> },
          { path: 'approvals', element: <ReviewQueuePage /> },
          { path: 'reports', element: <ReportsPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'audit', element: <AuditPage /> },
        ],
      },
    ],
  },
])
